import {
  Fragment,
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentRef,
} from 'react';
import { useRouter } from 'next/router';
import * as Accordion from '@radix-ui/react-accordion';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { useRect } from '@reach/rect';
import FocusTrap from 'focus-trap-react';

import { useNavTheme } from '#/lib/nav-theme';
import { runWhenIdle } from '#/lib/run-when-idle';
import type { SanityAnnouncement } from '#/lib/sanity/queries/announcement';
import type { SanityHeader } from '#/lib/sanity/queries/header';
import { SanityPage } from '#/lib/sanity/queries/pageProps';
import { trackDeferred, useAnalytics } from '#/lib/segment';
import { useAppSizes } from '#/lib/use-app-sizes';
import { cn, COLORS } from '#/lib/utils';
import { ChevronDown } from '#/components/ChevronDown';
import { Link } from '#/components/Link';
import { SvgIcon } from '#/components/SvgIcon';

import { Button } from '../Button';
import { Media } from '../Media';
import { HeaderWrapper } from './HeaderWrapper';
import { MegaMenuLayers } from './MegaMenuLayers';

type Props = SanityHeader & {
  announcement: SanityAnnouncement | null;
  menuBarStyle: SanityPage['menuBarStyle'];
};

const FEATURED_CONTENT_CTA: Record<string, string> = {
  post: 'View Article',
  caseStudy: 'View Case Study',
  resource: 'View Resource',
};

export function SiteHeader({
  primaryNavigation,
  secondaryNavigation,
  announcement,
  menuBarStyle,
}: Props) {
  const router = useRouter();
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [isTransitioning, setTransitioning] = useState(false);
  // Defer mounting the accordion tree until the first open so every page
  // doesn't pay hydration + DOM cost for the mobile menu up front.
  const [hasMountedMobileNav, setHasMountedMobileNav] = useState(false);
  // Only mount heavy accordion panel bodies while that section is expanded —
  // keeps hamburger open + accordion tap off the full mega-menu DOM cost.
  const [openMobileMenus, setOpenMobileMenus] = useState<string[]>([]);
  const isMobileNavOpenRef = useRef(false);
  const { windowWidth } = useAppSizes();
  const headerRef = useRef<ComponentRef<'header'>>(null);
  const headerRect = useRect(headerRef);
  const isMenuBarHidden = menuBarStyle === 'hidden';

  const toggleMobileNav = useCallback((open?: boolean) => {
    const next = open === undefined ? !isMobileNavOpenRef.current : open;
    if (next) {
      setHasMountedMobileNav(true);
    } else {
      // Drop expanded panels when closing so the next open starts light.
      setOpenMobileMenus([]);
    }
    isMobileNavOpenRef.current = next;
    startTransition(() => {
      setMobileNavOpen(next);
    });
  }, []);

  // Warm the mobile nav during idle time so the first hamburger tap doesn't
  // have to mount the accordion tree on the interaction critical path.
  useEffect(() => {
    if (hasMountedMobileNav) return;
    return runWhenIdle(() => setHasMountedMobileNav(true), 2000);
  }, [hasMountedMobileNav]);

  useEffect(() => {
    const handleChange = () => {
      toggleMobileNav(false);
    };

    router.events.on('routeChangeStart', handleChange);
    router.events.on('hashChangeStart', handleChange);

    return () => {
      router.events.off('routeChangeStart', handleChange);
      router.events.off('hashChangeStart', handleChange);
    };
  }, [router, toggleMobileNav]);

  const announcementRef = useRef<ComponentRef<'a'>>(null);
  const mobileNavRef = useRef<ComponentRef<'div'>>(null);

  const analytics = useAnalytics();

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', isMobileNavOpen);
    const mobileNavElement = mobileNavRef.current;

    const onTransitionStart = () => {
      setTransitioning(true);
    };

    const onTransitionEnd = () => {
      setTransitioning(false);
    };

    mobileNavElement?.addEventListener('transitionstart', onTransitionStart);
    mobileNavElement?.addEventListener('transitionend', onTransitionEnd);

    return () => {
      mobileNavElement?.removeEventListener(
        'transitionstart',
        onTransitionStart,
      );
      mobileNavElement?.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    if (!isMobileNavOpen) return;

    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        toggleMobileNav(false);
      }
    };

    document.addEventListener('keydown', onKeydown);

    return () => {
      document.removeEventListener('keydown', onKeydown);
    };
  }, [isMobileNavOpen, toggleMobileNav]);

  useEffect(() => {
    if (isMobileNavOpen && windowWidth >= 900) {
      isMobileNavOpenRef.current = false;
      setMobileNavOpen(false);
    }
  }, [isMobileNavOpen, windowWidth]);

  const { theme, isScrolled } = useNavTheme();

  const [openMenuValue, setOpenMenuValue] = useState('');
  // Mount each desktop mega-menu panel the first time it's opened, then keep
  // it. Avoids idle-warming *every* panel (heavy Media + MegaMenuLayers) while
  // still making re-hovers cheap.
  const [mountedDesktopMenus, setMountedDesktopMenus] = useState(
    () => new Set<string>(),
  );

  // Tint the indicator from menu data (no DOM measurement). Use the column
  // near the panel center — that's where the arrow typically sits.
  const openMenu = primaryNavigation.find(
    (item) => item._type === 'menu' && item.title === openMenuValue,
  );
  const arrowColor = (() => {
    if (!openMenu || openMenu._type !== 'menu') return undefined;
    const columns = openMenu.columns;
    if (!columns.length) return undefined;
    const mid = columns[Math.floor((columns.length - 1) / 2)]!;
    if (
      mid.sections.some(
        (section) =>
          section._type === 'featuredContent' ||
          section._type === 'featuredLink',
      )
    ) {
      return COLORS.cipher.DEFAULT;
    }
    if (mid.background === 'lightPurple') {
      return COLORS.cipher['50'];
    }
    if (mid.background === 'purple') {
      return COLORS.cipher.DEFAULT;
    }
    return undefined;
  })();

  const handleMenuValueChange = (value: string) => {
    // Keep mega-menu open off the urgent interaction path so hover paints
    // sooner while panel content mounts.
    startTransition(() => {
      setOpenMenuValue(value);
      if (value) {
        setMountedDesktopMenus((prev) => {
          if (prev.has(value)) return prev;
          const next = new Set(prev);
          next.add(value);
          return next;
        });
      }
    });
  };

  const showAnnouncement =
    announcement?.message && !router.pathname.includes('data-rights-unwrapped');

  return (
    <div className="contents">
      {showAnnouncement ? (
        <Link
          id="site-announcement"
          ref={announcementRef}
          href={announcement.link.href}
          target={announcement.link.target}
          onClick={() => {
            trackDeferred(analytics, 'Clicked Announcement', {
              message: announcement.message,
              href: announcement.link.href,
            });
          }}
          className="pointer-events-auto relative z-20 block px-15 pb-11 pt-10 text-center text-13 font-[440] leading-120 -tracking-1 m:px-25 m:text-14"
          style={{
            background: announcement.background,
            color: announcement.foreground,
          }}
        >
          <div className="mx-auto max-w-[295px] s:max-w-[972px]">
            {announcement.message}
          </div>
        </Link>
      ) : null}

      <HeaderWrapper
        isMobileNavOpen={isMobileNavOpen}
        headerRef={headerRef}
        isMenuBarHidden={isMenuBarHidden}
        className={cn(
          'pointer-events-auto sticky top-0 z-20 h-64 w-full px-7 pt-8 text-12 font-[440] leading-100 -tracking-1 ml:h-[8rem] ml:px-0 ml:pt-0 ml:text-14 ml:transition-[background-color,backdrop-filter,color]',
          {
            'ml:text-cloud': theme === 'purple' || theme === 'charcoal',
            // Transparent over heroes at the top of the page; frosted glass after
            // 100px of scroll (or while a mega menu is open so the bar stays readable).
            'ml:backdrop-blur-[10px] ml:bg-cloud/60':
              theme === 'light' && (isScrolled || Boolean(openMenuValue)),
            'ml:backdrop-blur-[10px] ml:bg-charcoal/60':
              (theme === 'purple' || theme === 'charcoal') &&
              (isScrolled || Boolean(openMenuValue)),
          },
        )}
      >
        <>
          <NavigationMenu.Root
            className="NavigationMenuRoot"
            delayDuration={150}
            onValueChange={handleMenuValueChange}
          >
            <NavigationMenu.List className="NavigationMenuList text-14">
              {primaryNavigation.map((item) => {
                return (
                  <NavigationMenu.Item
                    key={`${item._key}-desktop`}
                    value={item.title}
                    className="h-full"
                  >
                    {item._type === 'menu' ? (
                      <>
                        <NavigationMenu.Trigger className="NavigationMenuTrigger">
                          <span>{item.title}</span>{' '}
                          <ChevronDown className="ChevronDown" />
                        </NavigationMenu.Trigger>
                        {(mountedDesktopMenus.has(item.title) ||
                          openMenuValue === item.title) && (
                          <NavigationMenu.Content className="NavigationMenuContent text-charcoal">
                            {/* Cap to the viewport (ViewportPosition has 2.5rem side padding) so wide menus compress on smaller screens instead of overflowing. */}
                            <div className="max-w-[calc(100vw-5rem)] px-40">
                              {/* 48px vertical padding matches the colored blocks' internal padding so all column titles align at the top. */}
                              <div className="flex gap-x-40 py-48">
                                {item.columns.map((column, i) => {
                                  const isLastColumn =
                                    i === item.columns.length - 1;
                                  const isFirstColumn = i === 0;
                                  const isLightPurpleBackground =
                                    column.background === 'lightPurple';
                                  const isPurpleBackground =
                                    column.background === 'purple';
                                  const hasBackground =
                                    isLightPurpleBackground ||
                                    isPurpleBackground;
                                  const hasFeaturedLink = column.sections.some(
                                    (section) =>
                                      section._type === 'featuredLink',
                                  );
                                  // A colored column sitting next to a featured panel or another colored column should touch it (no white gutter).
                                  const prevColumn =
                                    i > 0 ? item.columns[i - 1] : null;
                                  const prevColumnHasFeaturedPanel =
                                    !!prevColumn &&
                                    prevColumn.sections.some(
                                      (section) =>
                                        section._type === 'featuredContent' ||
                                        section._type === 'featuredLink',
                                    );
                                  const prevColumnHasBackground =
                                    prevColumn?.background === 'lightPurple' ||
                                    prevColumn?.background === 'purple';
                                  // Safety ceiling: a very long eyebrow title widens its column only up to this, then wraps. Scaled by column count so a full menu can't outgrow the viewport, and generous for sparse menus. Plain pixel values (not %) so the intrinsic measurement respects them and the menu never reserves dead space. Background columns get +96px for their own padding.
                                  const columnCount = item.columns.length;
                                  const columnCap =
                                    columnCount <= 2
                                      ? hasBackground
                                        ? 'max-w-[596px]'
                                        : 'max-w-[500px]'
                                      : columnCount === 3
                                        ? hasBackground
                                          ? 'max-w-[496px]'
                                          : 'max-w-[400px]'
                                        : hasBackground
                                          ? 'max-w-[396px]'
                                          : 'max-w-[300px]';
                                  return (
                                    <Fragment key={`${column._key}-desktop`}>
                                      <div
                                        className={cn(
                                          'min-w-0 flex-auto space-y-30',
                                          columnCap,
                                          {
                                            // A featured-link panel grows to fill the column, so the column must be a flex column. The negative bottom margin bleeds it to the menu's bottom edge (1px of overdraw avoids a sub-pixel hairline of the white viewport showing through; the viewport clips it).
                                            'flex flex-col -mb-[49px]':
                                              hasFeaturedLink,
                                            // Bleed the colored panel to the top/bottom edges (cancels the row's py-48) and, at the ends, to the side edge.
                                            'relative -mt-48 -mb-[49px] p-48':
                                              hasBackground,
                                            'bg-cipher-50':
                                              isLightPurpleBackground,
                                            'bg-cipher text-cloud':
                                              isPurpleBackground,
                                            // Cancel the row's gap-x-40 / edge padding so colored panels meet each other and the menu edges.
                                            // Only pull from the left when abutting a previous colored column — applying -mr on the previous column as well would overlap by a full gap.
                                            '-ml-40':
                                              hasBackground &&
                                              (isFirstColumn ||
                                                prevColumnHasFeaturedPanel ||
                                                prevColumnHasBackground),
                                            '-mr-40':
                                              hasBackground && isLastColumn,
                                          },
                                        )}
                                      >
                                        {column.sections.map(
                                          (section, sectionIndex) => {
                                            if (section._type === 'linkGroup') {
                                              return (
                                                <div
                                                  key={`${section._key}-desktop`}
                                                  // Sizes to its widest child (w-max = max-content, so the eyebrow title never wraps and defines the column when it's the longest line). Links and descriptions below carry their own 250px caps so long copy wraps instead of inflating the menu. max-w-full lets everything compress on small screens.
                                                  className="w-max min-w-[160px] max-w-full space-y-24"
                                                >
                                                  {section.libraryIcon?.svg ? (
                                                    <SvgIcon
                                                      svg={
                                                        section.libraryIcon.svg
                                                      }
                                                      className={cn(
                                                        '[&_svg]:size-32',
                                                        isPurpleBackground
                                                          ? 'text-cloud'
                                                          : 'text-cipher',
                                                      )}
                                                    />
                                                  ) : section.icon ? (
                                                    <Media
                                                      media={section.icon}
                                                      className="size-32"
                                                      mediaClassName="object-contain"
                                                      sizes="32px"
                                                    />
                                                  ) : null}
                                                  <h3
                                                    className={cn(
                                                      'type-eyebrow',
                                                      isPurpleBackground
                                                        ? 'text-cloud'
                                                        : 'text-cipher',
                                                    )}
                                                  >
                                                    {section.title}
                                                  </h3>
                                                  <ul className="max-w-[250px] space-y-20">
                                                    {section.items.map(
                                                      (item) => {
                                                        const isPurpleArrow =
                                                          item.style ===
                                                          'purpleArrow';
                                                        return (
                                                          <li
                                                            key={`${item._key}-desktop`}
                                                          >
                                                            {isPurpleArrow ? (
                                                              <>
                                                                <NavigationMenu.Link
                                                                  asChild
                                                                >
                                                                  <Button
                                                                    variant="link"
                                                                    href={
                                                                      item.link
                                                                        .href
                                                                    }
                                                                    target={
                                                                      item.link
                                                                        .target
                                                                    }
                                                                    className={cn(
                                                                      'type-nav font-medium',
                                                                      isPurpleBackground
                                                                        ? 'text-cloud'
                                                                        : 'text-cipher',
                                                                    )}
                                                                  >
                                                                    {
                                                                      item.link
                                                                        .title
                                                                    }
                                                                  </Button>
                                                                </NavigationMenu.Link>
                                                                {item.description ? (
                                                                  <p
                                                                    className={cn(
                                                                      'mt-8 text-12 font-light leading-150 tracking-0',
                                                                      isPurpleBackground
                                                                        ? 'text-cloud/80'
                                                                        : undefined,
                                                                    )}
                                                                  >
                                                                    {
                                                                      item.description
                                                                    }
                                                                  </p>
                                                                ) : null}
                                                              </>
                                                            ) : (
                                                              <NavigationMenu.Link
                                                                asChild
                                                              >
                                                                <Link
                                                                  href={
                                                                    item.link
                                                                      .href
                                                                  }
                                                                  target={
                                                                    item.link
                                                                      .target
                                                                  }
                                                                  className={cn(
                                                                    'group relative block rounded-3 type-nav outline-none focus-visible:ring-2 focus-visible:ring-cipher focus-visible:ring-offset-4',
                                                                    isPurpleBackground
                                                                      ? 'text-cloud focus-visible:ring-offset-cipher'
                                                                      : 'text-charcoal focus-visible:ring-offset-cloud',
                                                                  )}
                                                                >
                                                                  <div
                                                                    className={cn(
                                                                      'absolute -inset-6 scale-95 rounded-6 opacity-0 transition group-hover:scale-100 group-hover:opacity-75',
                                                                      isPurpleBackground
                                                                        ? 'bg-cloud/10'
                                                                        : 'bg-grayscale-01',
                                                                    )}
                                                                  ></div>
                                                                  <div className="relative flex items-center">
                                                                    {item.icon ? (
                                                                      <Media
                                                                        media={
                                                                          item.icon
                                                                        }
                                                                        className="mr-10 size-35"
                                                                        mediaClassName="object-contain"
                                                                        sizes="35px"
                                                                      />
                                                                    ) : null}
                                                                    {
                                                                      item.link
                                                                        .title
                                                                    }
                                                                  </div>
                                                                  {item.description ? (
                                                                    <p
                                                                      className={cn(
                                                                        'relative mt-8 text-12 font-light leading-150 tracking-0',
                                                                        isPurpleBackground &&
                                                                          'text-cloud/80',
                                                                      )}
                                                                    >
                                                                      {
                                                                        item.description
                                                                      }
                                                                    </p>
                                                                  ) : null}
                                                                </Link>
                                                              </NavigationMenu.Link>
                                                            )}
                                                          </li>
                                                        );
                                                      },
                                                    )}
                                                  </ul>
                                                  {section.description ? (
                                                    <p
                                                      className={cn(
                                                        'max-w-[250px] type-paragraph-sm',
                                                        isPurpleBackground
                                                          ? 'text-cloud/80'
                                                          : 'text-grayscale-05',
                                                      )}
                                                    >
                                                      {section.description}
                                                    </p>
                                                  ) : null}
                                                </div>
                                              );
                                            } else if (
                                              section._type ===
                                              'featuredContent'
                                            ) {
                                              return (
                                                <NavigationMenu.Link
                                                  asChild
                                                  key={`${section._key}-desktop`}
                                                >
                                                  <Link
                                                    className={cn(
                                                      // Height compensates for the menu's py-48 so the panel bleeds to the top and bottom edges (+1px overdraw against sub-pixel hairlines).
                                                      'group relative -my-48 flex h-[calc(100%+97px)] w-[320px] flex-col justify-center overflow-hidden bg-cipher p-48 text-cloud outline-none',
                                                      // The max-width cap (for compression on small screens) must include the 40px edge bleed, or it cancels it.
                                                      isLastColumn
                                                        ? '-mr-40 max-w-[calc(100%+40px)]'
                                                        : 'max-w-full',
                                                    )}
                                                    href={section.href}
                                                  >
                                                    <MegaMenuLayers className="pointer-events-none absolute left-[-76px] top-[92px]" />
                                                    <div className="relative flex w-full flex-col gap-16">
                                                      <Media
                                                        className="block aspect-[8/5] w-full overflow-hidden rounded-8"
                                                        media={
                                                          section.thumbnailImage ??
                                                          section.referenceThumbnailImage
                                                        }
                                                        aspect={false}
                                                        placeholder={true}
                                                        sizes="200px"
                                                      />
                                                      <div className="flex flex-col gap-16">
                                                        {section.cardBadge ? (
                                                          <p className="type-eyebrow text-cloud">
                                                            {section.cardBadge}
                                                          </p>
                                                        ) : null}
                                                        <p className="type-paragraph-sm text-grayscale-02">
                                                          {section.headline ??
                                                            section.referenceTitle}
                                                        </p>
                                                      </div>
                                                      <Button
                                                        asDiv
                                                        variant="link"
                                                        className="text-cloud"
                                                      >
                                                        {section.ctaLabel ??
                                                          FEATURED_CONTENT_CTA[
                                                            section
                                                              .referenceType
                                                          ] ??
                                                          'Learn More'}
                                                      </Button>
                                                    </div>
                                                  </Link>
                                                </NavigationMenu.Link>
                                              );
                                            } else if (
                                              section._type === 'featuredLink'
                                            ) {
                                              return (
                                                <NavigationMenu.Link
                                                  asChild
                                                  key={`${section._key}-desktop`}
                                                >
                                                  <Link
                                                    className={cn(
                                                      // Grows to fill the remaining column space; negative margins bleed it to the menu edges (cancelling the row's py-48 / px-40).
                                                      'group relative flex w-[320px] flex-1 flex-col overflow-hidden bg-cipher p-48 text-cloud outline-none',
                                                      // The max-width cap (for compression on small screens) must include the 40px edge bleed, or it cancels it.
                                                      isFirstColumn ||
                                                        isLastColumn
                                                        ? 'max-w-[calc(100%+40px)]'
                                                        : 'max-w-full',
                                                      {
                                                        '-mt-48':
                                                          sectionIndex === 0,
                                                        '-ml-40': isFirstColumn,
                                                        '-mr-40': isLastColumn,
                                                      },
                                                    )}
                                                    href={section.link.href}
                                                    target={section.link.target}
                                                  >
                                                    <MegaMenuLayers className="pointer-events-none absolute left-[-76px] top-[92px]" />
                                                    <div className="relative flex w-full flex-col gap-16">
                                                      <div className="flex flex-col gap-16">
                                                        <p className="type-paragraph-lg font-medium text-cloud">
                                                          {section.title}
                                                        </p>
                                                        <p className="type-paragraph-sm text-grayscale-02">
                                                          {section.description}
                                                        </p>
                                                      </div>
                                                      <Button
                                                        asDiv
                                                        variant="link"
                                                        className="text-cloud"
                                                      >
                                                        {section.link.title}
                                                      </Button>
                                                    </div>
                                                  </Link>
                                                </NavigationMenu.Link>
                                              );
                                            }
                                          },
                                        )}
                                        {!column.sections.some(
                                          (section) =>
                                            section._type ===
                                              'featuredContent' ||
                                            section._type === 'featuredLink',
                                        ) && column.textLink ? (
                                          <Button
                                            variant="link"
                                            // Never let the arrow (or the label) wrap when the column compresses; the link may overflow into the column gap instead.
                                            className={cn(
                                              'whitespace-nowrap',
                                              isPurpleBackground &&
                                                'text-cloud',
                                            )}
                                            href={column.textLink.href}
                                            target={column.textLink.target}
                                          >
                                            {column.textLink.title}
                                          </Button>
                                        ) : null}
                                      </div>
                                      {!isLastColumn && column.hasDivider ? (
                                        <div className="w-1 flex-shrink-0 bg-cipher-01" />
                                      ) : null}
                                    </Fragment>
                                  );
                                })}
                              </div>
                            </div>
                          </NavigationMenu.Content>
                        )}
                      </>
                    ) : (
                      <NavigationMenu.Link asChild>
                        <Link
                          className="NavigationMenuLink"
                          href={item.href}
                          target={item.target}
                        >
                          {item.title}
                        </Link>
                      </NavigationMenu.Link>
                    )}
                  </NavigationMenu.Item>
                );
              })}
            </NavigationMenu.List>
            <nav className="ml-auto flex items-center gap-x-30 pl-45 text-14">
              {secondaryNavigation?.map((item, i) => {
                const isLast = i === secondaryNavigation.length - 1;
                const Component = isLast ? Button : Link;
                return (
                  <Component
                    key={`${item._key}-desktop`}
                    href={item.href}
                    target={item.target}
                    className={cn('transition-colors', {
                      'hover:text-grayscale-04': !isLast,
                      'ml:rounded-8 ml:px-24 ml:py-17': isLast,
                    })}
                    variant="primary"
                  >
                    {item.title}
                  </Component>
                );
              })}
            </nav>
            <div className="ViewportPosition">
              <NavigationMenu.Viewport className="NavigationMenuViewport" />
            </div>
            <NavigationMenu.Indicator className="NavigationMenuIndicator">
              <div className="Arrow" style={{ backgroundColor: arrowColor }} />
            </NavigationMenu.Indicator>
          </NavigationMenu.Root>

          <div className="flex items-center ml:hidden">
            <button
              className="relative space-y-5 rounded-2 outline-none before:absolute before:left-1/2 before:top-1/2 before:h-40 before:w-48 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-10 focus-visible:ring-2 focus-visible:ring-cipher focus-visible:ring-offset-4 focus-visible:ring-offset-cloud ml:hidden"
              onClick={() => toggleMobileNav()}
            >
              <div
                className={cn(
                  'relative h-2 w-23 rounded-full bg-charcoal transition-transform',
                  {
                    'translate-y-7 -rotate-45': isMobileNavOpen,
                  },
                )}
              ></div>
              <div
                className={cn(
                  'relative h-2 w-23 rounded-full bg-charcoal transition-opacity duration-250',
                  {
                    'opacity-0': isMobileNavOpen,
                  },
                )}
              ></div>
              <div
                className={cn(
                  'relative h-2 w-23 rounded-full bg-charcoal transition-transform',
                  {
                    '-translate-y-7 rotate-45': isMobileNavOpen,
                  },
                )}
              ></div>
            </button>
          </div>
        </>
      </HeaderWrapper>

      {/* Mobile Nav — mounted on first open, then kept for close animation.
            FocusTrap is scoped to this panel only so activating it doesn't walk
            the idle-warmed desktop mega-menu DOM on hamburger open. */}
      {hasMountedMobileNav ? (
        <FocusTrap
          active={isMobileNavOpen}
          focusTrapOptions={{
            escapeDeactivates: false,
            preventScroll: true,
            // Hamburger (and page content) sit outside this trap.
            allowOutsideClick: true,
          }}
        >
          <div
            className={cn(
              'fixed inset-x-0 bottom-0 z-20 flex-grow overflow-y-auto px-7 pb-7 ml:hidden',
              {
                invisible: !isTransitioning && !isMobileNavOpen,
              },
            )}
            style={{
              top: headerRect?.bottom ?? 0,
            }}
          >
            <nav
              ref={mobileNavRef}
              className={cn(
                'origin-top-center rounded-b-15 border-x-1 border-b-1 border-grayscale-02 bg-cloud transition duration-400',
                {
                  '-translate-y-full scale-95 opacity-0': !isMobileNavOpen,
                  invisible: !isTransitioning && !isMobileNavOpen,
                },
              )}
            >
              <Accordion.Root
                type="multiple"
                value={openMobileMenus}
                onValueChange={(value) =>
                  startTransition(() => setOpenMobileMenus(value))
                }
              >
                {primaryNavigation.map((item) => {
                  if (item._type === 'menu') {
                    const isAccordionOpen = openMobileMenus.includes(item._key);
                    return (
                      <Accordion.Item
                        value={item._key}
                        key={`${item._key}-mobile`}
                      >
                        <Accordion.Header className="sticky top-0 z-1 border-b-1 border-grayscale-02">
                          <Accordion.Trigger className="MobileNavTrigger">
                            <span>{item.title}</span>
                            <div className="flex w-23 justify-center">
                              <ChevronDown className="MobileChevronDown" />
                            </div>
                          </Accordion.Trigger>
                        </Accordion.Header>
                        <Accordion.Content className="space-y-30 overflow-hidden border-b-1 border-grayscale-02 px-20 pb-25 pt-20">
                          {isAccordionOpen
                            ? item.columns.map((column, columnIndex) => {
                                const isLightPurpleBackground =
                                  column.background === 'lightPurple';
                                const isPurpleBackground =
                                  column.background === 'purple';
                                const hasBackground =
                                  isLightPurpleBackground || isPurpleBackground;
                                const hasFeaturedLink = column.sections.some(
                                  (section) => section._type === 'featuredLink',
                                );
                                const nextColumnBackground =
                                  item.columns[columnIndex + 1]?.background;
                                const nextColumnHasBackground =
                                  nextColumnBackground === 'lightPurple' ||
                                  nextColumnBackground === 'purple';
                                const isLastColumn =
                                  columnIndex === item.columns.length - 1;
                                return (
                                  <Fragment key={`${column._key}-mobile`}>
                                    <div
                                      className={cn('space-y-30', {
                                        // Full-bleed colored panel (cancels the accordion content's px-20).
                                        '-mx-20 p-20': hasBackground,
                                        'bg-cipher-50': isLightPurpleBackground,
                                        'bg-cipher text-cloud':
                                          isPurpleBackground,
                                        // Pull the following colored block flush against this full-bleed purple panel (the -30 bottom margin collapses with the sibling's +30 from space-y-30).
                                        '-mb-30':
                                          hasFeaturedLink &&
                                          nextColumnHasBackground,
                                      })}
                                    >
                                      {column.sections.map((section) => {
                                        if (section._type === 'linkGroup') {
                                          return (
                                            <div
                                              key={`${section._key}-mobile`}
                                              className="space-y-20"
                                            >
                                              <div className="flex items-center gap-12">
                                                {section.libraryIcon?.svg ? (
                                                  <SvgIcon
                                                    svg={
                                                      section.libraryIcon.svg
                                                    }
                                                    className={cn(
                                                      '[&_svg]:size-28',
                                                      isPurpleBackground
                                                        ? 'text-cloud'
                                                        : 'text-cipher',
                                                    )}
                                                  />
                                                ) : section.icon ? (
                                                  <Media
                                                    media={section.icon}
                                                    className="size-28"
                                                    mediaClassName="object-contain"
                                                    sizes="28px"
                                                  />
                                                ) : null}
                                                <h3
                                                  className={cn(
                                                    'type-eyebrow',
                                                    isPurpleBackground
                                                      ? 'text-cloud'
                                                      : 'text-cipher',
                                                  )}
                                                >
                                                  {section.title}
                                                </h3>
                                              </div>
                                              <ul className="space-y-20">
                                                {section.items.map((item) => {
                                                  const isPurpleArrow =
                                                    item.style ===
                                                    'purpleArrow';
                                                  return (
                                                    <li
                                                      key={`${item._key}-mobile`}
                                                    >
                                                      {isPurpleArrow ? (
                                                        <Button
                                                          variant="link"
                                                          href={item.link.href}
                                                          target={
                                                            item.link.target
                                                          }
                                                          className={cn(
                                                            'type-nav font-medium',
                                                            isPurpleBackground
                                                              ? 'text-cloud'
                                                              : 'text-cipher',
                                                          )}
                                                        >
                                                          {item.link.title}
                                                        </Button>
                                                      ) : (
                                                        <Link
                                                          href={item.link.href}
                                                          target={
                                                            item.link.target
                                                          }
                                                          className={cn(
                                                            'block rounded-3 type-nav outline-none focus-visible:ring-2 focus-visible:ring-cipher focus-visible:ring-offset-4',
                                                            isPurpleBackground
                                                              ? 'text-cloud focus-visible:ring-offset-cipher'
                                                              : 'text-charcoal focus-visible:ring-offset-cloud',
                                                          )}
                                                        >
                                                          <div className="flex items-center">
                                                            {item.icon ? (
                                                              <Media
                                                                media={
                                                                  item.icon
                                                                }
                                                                className="mr-10 size-35"
                                                                mediaClassName="object-contain"
                                                                sizes="35px"
                                                              />
                                                            ) : null}
                                                            {item.link.title}
                                                          </div>
                                                          {item.description ? (
                                                            <p
                                                              className={cn(
                                                                'mt-8 text-12 font-light leading-150 tracking-0',
                                                                isPurpleBackground &&
                                                                  'text-cloud/80',
                                                              )}
                                                            >
                                                              {item.description}
                                                            </p>
                                                          ) : null}
                                                        </Link>
                                                      )}
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                              {section.description ? (
                                                <p
                                                  className={cn(
                                                    'type-paragraph-sm',
                                                    isPurpleBackground
                                                      ? 'text-cloud/80'
                                                      : 'text-grayscale-05',
                                                  )}
                                                >
                                                  {section.description}
                                                </p>
                                              ) : null}
                                            </div>
                                          );
                                        } else if (
                                          section._type === 'featuredContent'
                                        ) {
                                          return (
                                            <Link
                                              // Full-bleed purple promo card, mirroring the desktop featured panel (cancels the accordion content's px-20, and its pb-25 when last).
                                              className={cn(
                                                'relative -mx-20 block overflow-hidden bg-cipher p-25 text-cloud',
                                                {
                                                  '-mb-25': isLastColumn,
                                                },
                                              )}
                                              href={section.href}
                                              key={`${section._key}-mobile`}
                                            >
                                              <div className="relative flex flex-col gap-16">
                                                <Media
                                                  className="block aspect-[8/5] w-full overflow-hidden rounded-8"
                                                  media={
                                                    section.thumbnailImage ??
                                                    section.referenceThumbnailImage
                                                  }
                                                  aspect={false}
                                                  placeholder={true}
                                                  sizes="350px"
                                                />
                                                {section.cardBadge ? (
                                                  <p className="type-eyebrow text-cloud">
                                                    {section.cardBadge}
                                                  </p>
                                                ) : null}
                                                <p className="type-paragraph-sm text-grayscale-02">
                                                  {section.headline ??
                                                    section.referenceTitle}
                                                </p>
                                                <Button
                                                  asDiv
                                                  variant="link"
                                                  className="text-cloud"
                                                >
                                                  {section.ctaLabel ??
                                                    FEATURED_CONTENT_CTA[
                                                      section.referenceType
                                                    ] ??
                                                    'Learn More'}
                                                </Button>
                                              </div>
                                            </Link>
                                          );
                                        } else if (
                                          section._type === 'featuredLink'
                                        ) {
                                          return (
                                            <Link
                                              // Full-bleed purple panel (cancels the accordion content's px-20/pt-20).
                                              className="relative -mx-20 -mt-20 block overflow-hidden bg-cipher p-25 text-cloud"
                                              href={section.link.href}
                                              target={section.link.target}
                                              key={`${section._key}-mobile`}
                                            >
                                              <div className="relative space-y-16">
                                                <h3 className="text-18 font-[440] leading-116 -tracking-2">
                                                  {section.title}
                                                </h3>
                                                <p className="text-13 font-light leading-150 tracking-0 text-grayscale-02">
                                                  {section.description}
                                                </p>
                                              </div>
                                              <Button
                                                asDiv
                                                variant="link"
                                                className="relative mt-16 text-cloud"
                                              >
                                                {section.link.title}
                                              </Button>
                                            </Link>
                                          );
                                        }
                                      })}
                                      {!column.sections.some(
                                        (section) =>
                                          section._type === 'featuredContent' ||
                                          section._type === 'featuredLink',
                                      ) && column.textLink ? (
                                        <Button
                                          variant="link"
                                          className={cn(
                                            isPurpleBackground && 'text-cloud',
                                          )}
                                          href={column.textLink.href}
                                          target={column.textLink.target}
                                        >
                                          {column.textLink.title}
                                        </Button>
                                      ) : null}
                                    </div>
                                    {column.hasDivider ? (
                                      <div className="my-20 h-1 w-full bg-cipher-01"></div>
                                    ) : null}
                                  </Fragment>
                                );
                              })
                            : null}
                        </Accordion.Content>
                      </Accordion.Item>
                    );
                  } else {
                    return (
                      <Link
                        key={`${item._key}-mobile`}
                        href={item.href}
                        target={item.target}
                      >
                        {item.title}
                      </Link>
                    );
                  }
                })}
              </Accordion.Root>

              <div className="divide-y-1 divide-grayscale-02">
                {secondaryNavigation.map((item, i) => {
                  const isLast = i === secondaryNavigation.length - 1;

                  if (isLast) {
                    return (
                      <div
                        key={`${item._key}-mobile`}
                        className="border-t-1 border-t-grayscale-02 px-20 pb-20 pt-25"
                      >
                        <Link
                          href={item.href}
                          target={item.target}
                          className="block rounded-15 bg-charcoal p-15 text-center text-22 font-[440] leading-100 -tracking-1 text-cloud"
                        >
                          {item.title}
                        </Link>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={`${item._key}-mobile`}
                      href={item.href}
                      target={item.target}
                      className="MobileNavLink"
                    >
                      {item.title}
                    </Link>
                  );
                })}
              </div>
            </nav>
          </div>
        </FocusTrap>
      ) : null}
    </div>
  );
}
